import { useState, useEffect, useCallback } from 'react';
import { Smartphone, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import brandConfig from '../../config/brandConfig';

const screenshots = [
  { src: '/mobile-app/1.jpeg', alt: 'Pantalla principal - Encuentra tu lote ideal' },
  { src: '/mobile-app/2.jpeg', alt: 'Mapa interactivo con detalle de lotes' },
  { src: '/mobile-app/3.jpeg', alt: 'Vista satelital del proyecto' },
  { src: '/mobile-app/4.jpeg', alt: 'Contacto directo por WhatsApp' },
  { src: '/mobile-app/5.jpeg', alt: 'Vista de mapa del proyecto' },
  { src: '/mobile-app/6.jpeg', alt: 'Detalle del lote con agentes' },
];

const PhoneMockup = ({ src, alt, className = '' }) => (
  <div className={`relative flex-shrink-0 ${className}`}>
    <div className="relative bg-gray-950 rounded-[2.5rem] p-1.5 shadow-2xl shadow-black/40">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-950 rounded-b-2xl z-10"></div>
      <div className="rounded-[2rem] overflow-hidden bg-white aspect-[9/19.5]">
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      </div>
    </div>
  </div>
);

const MobileAppSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const nextSlide = useCallback(() => {
    setActiveIndex((i) => (i === screenshots.length - 1 ? 0 : i + 1));
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 3000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const getIndex = (offset) => (activeIndex + offset + screenshots.length) % screenshots.length;

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden" style={{
      background: 'linear-gradient(135deg, #064e3b 0%, #065f46 40%, #047857 100%)'
    }}>
      {/* Decorative */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left - Text */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full mb-6">
              <Smartphone className="w-4 h-4" />
              <span className="text-sm font-bold">App Movil</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight tracking-tight">
              Tus lotes en las manos de{' '}
              <span className="font-display italic text-emerald-300">tus clientes</span>
            </h2>

            <p className="text-lg text-emerald-100/80 mb-4 leading-relaxed">
              <strong className="text-white">Vende mas rapido.</strong>{' '}
              Ubicate en tus lotes con tu GPS y evita estar buscando donde es el lote.
            </p>

            <p className="text-base text-emerald-200/50 mb-8">
              Con la app movil de {brandConfig.name}, tus clientes exploran lotes en el mapa,
              ven la disponibilidad en tiempo real y te contactan al instante por WhatsApp.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              <button
                onClick={() => navigate('/app-movil')}
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-white text-emerald-900 font-bold rounded-xl hover:bg-emerald-50 transition-all duration-300 shadow-xl shadow-black/20 hover:scale-[1.02]"
              >
                Descubre el app
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <a
                href="https://play.google.com/store/apps/details?id=com.lotespro.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 0 1 0 1.732l-2.808 1.626L15.206 12l2.492-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                <span className="text-sm font-bold text-white">Descargar en Google Play</span>
              </a>
            </div>
          </div>

          {/* Right - Carousel */}
          <div className="flex items-end justify-center pb-8">
            <div className="relative flex items-end gap-3 sm:gap-5">
              <div className="hidden sm:block opacity-30 scale-[0.75] transition-all duration-500 -translate-y-10">
                <PhoneMockup src={screenshots[getIndex(-1)].src} alt={screenshots[getIndex(-1)].alt} className="w-36" />
              </div>
              <div className="transition-all duration-500 z-10 transform scale-100">
                <PhoneMockup src={screenshots[activeIndex].src} alt={screenshots[activeIndex].alt} className="w-48 sm:w-56" />
              </div>
              <div className="hidden sm:block opacity-30 scale-[0.75] transition-all duration-500 -translate-y-10">
                <PhoneMockup src={screenshots[getIndex(1)].src} alt={screenshots[getIndex(1)].alt} className="w-36" />
              </div>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-10">
          {screenshots.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === activeIndex ? 'w-8 bg-emerald-300' : 'w-2 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Blobs */}
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-emerald-400/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-teal-400/10 rounded-full blur-3xl"></div>
    </section>
  );
};

export default MobileAppSection;
